/**
 * Resolution director (Phase 40.1): decide, before a flight plays, how much of
 * it is worth watching. Every flight is precomputed at launch and the impact
 * resolver is pure, so the outcome is known on frame 0. The director scores it
 * and picks a tier:
 *   highlight — a direct hit, a kill, or damage with a bend around a body or a
 *               long flight: full speed, missile cam if the viewer can see it;
 *   routine   — damage, a near miss, a probe scan, or the viewer's own shot:
 *               brisk fast-forward, camera stays where it is;
 *   dud       — nothing happens and nobody the viewer cares about is involved:
 *               very fast, a one-line card at impact.
 * Everything is viewer-relative: a flight the viewer could not see is never
 * promoted by what it did to someone else, and the director never lingers on
 * an unseen enemy shot (that would telegraph it). Pure; main.ts gathers the
 * inputs and applies the pace.
 */

import type { ResolutionPace } from '../types';
export type { ResolutionPace };
export type ShotTier = 'highlight' | 'routine' | 'dud';

export interface ShotPreviewInput {
  /** Flight ended on a player hitbox. */
  hitPlayerId: number;
  /** Predicted by a dry run of the impact resolver (null: no terrain impact — lost, star, wormhole). */
  predictedDamage: number;
  predictedKills: number;
  /** Bodies the shot bent around (shotRecord.analyzeFlight). */
  assists: number;
  flightSeconds: number;
  /** The shooter is on the viewer's team. */
  ownTeam: boolean;
  /** Fraction of the flight path the viewer can see (1 when fog is off). */
  visibleFraction: number;
  /** Impact lands within 1.5 blast radii of some unit. */
  nearMiss: boolean;
  /** A probe or other reveal-on-flight weapon. */
  reveals: boolean;
  /** Predicted damage includes a unit on the viewer's team. */
  viewerTeamDamaged: boolean;
}

export interface ShotPreview {
  tier: ShotTier;
  /** Playback speed multiplier. */
  speed: number;
  /** Follow the shot with the missile cam. */
  missileCam: boolean;
  reason: string;
}

export type DirectorPace = Exclude<ResolutionPace, 'full'>;
/** Playback speed per tier for each director setting (owner: the first cut, 1/4/16, read as too fast). */
export const PACE_SPEEDS: Record<DirectorPace, Record<ShotTier, number>> = {
  relaxed:     { highlight: 1, routine: 2, dud: 6 },     // everything still watchable, duds just hurry
  brisk:       { highlight: 1, routine: 3, dud: 10 },    // the balanced default
  fastForward: { highlight: 1, routine: 4, dud: 16 },    // get to the next decision
};
export const DIRECTOR_SPEEDS = PACE_SPEEDS.brisk;
export const PACE_LABELS: Record<ResolutionPace, string> = { full: 'WATCH ALL', relaxed: 'RELAXED', brisk: 'BRISK', fastForward: 'FAST FWD' };
export const PACE_ORDER: ResolutionPace[] = ['full', 'relaxed', 'brisk', 'fastForward'];
export function nextPace(p: ResolutionPace): ResolutionPace { return PACE_ORDER[(PACE_ORDER.indexOf(p) + 1) % PACE_ORDER.length]; }
/** Highlight threshold on flight time (seconds), matching shotRecord.isHighlightShot. */
export const HIGHLIGHT_FLIGHT_SECONDS = 20;
/** Below this much visible path the viewer is not really watching the flight. */
export const VISIBLE_FRACTION_WATCHABLE = 0.3;
/** Missile cam only when most of the flight is in view. */
export const VISIBLE_FRACTION_MISSILE_CAM = 0.5;

export function classifyShot(i: ShotPreviewInput, pace: DirectorPace = 'brisk'): ShotPreview {
  const watchable = i.visibleFraction >= VISIBLE_FRACTION_WATCHABLE;
  const spectacular = i.hitPlayerId >= 0 || i.predictedKills > 0
    || (i.predictedDamage > 0 && (i.assists >= 1 || i.flightSeconds >= HIGHLIGHT_FLIGHT_SECONDS));
  const consequential = i.predictedDamage > 0 || i.nearMiss || i.reveals;

  let tier: ShotTier;
  let reason: string;
  if (spectacular && (watchable || i.ownTeam)) {
    tier = 'highlight';
    reason = i.hitPlayerId >= 0 ? 'direct hit' : i.predictedKills > 0 ? 'kill' : i.assists >= 1 ? 'gravity assist' : 'long flight';
  } else if (i.ownTeam) {
    tier = 'routine';                                   // you aimed it: you always get to see where it went
    reason = consequential ? 'own shot' : 'own shot, no effect';
  } else if (!watchable) {
    // Unseen enemy flight: the viewer only experiences the outcome
    tier = i.viewerTeamDamaged ? 'routine' : 'dud';
    reason = i.viewerTeamDamaged ? 'unseen, hits us' : 'unseen';
  } else if (consequential) {
    tier = 'routine';
    reason = i.predictedDamage > 0 ? 'damage' : i.nearMiss ? 'near miss' : 'scan';
  } else {
    tier = 'dud';
    reason = 'no effect';
  }
  return {
    tier,
    speed: PACE_SPEEDS[pace][tier],
    missileCam: tier === 'highlight' && i.visibleFraction >= VISIBLE_FRACTION_MISSILE_CAM,
    reason,
  };
}

/** One-line card for a dud under the director. */
export function describeDud(shooterName: string, weaponName: string, lost: boolean): string {
  return lost ? `${shooterName}: ${weaponName} lost in space` : `${shooterName}: ${weaponName} — no effect`;
}
